import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import assistantApi from '@services/assistant';

const AssistantContext = createContext();

const getStorageKey = (userId) => {
	if (!userId) return null;
	return `unify_assistant_session_${userId}`;
};

const generateSessionId = () => `assistant_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const AssistantProvider = ({ children, mode = 'general', currentShop, userId = null }) => {
	const [messages, setMessages] = useState([]);
	const [isOpen, setIsOpen] = useState(true);
	const [isMobileOpen, setIsMobileOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [sessionId, setSessionId] = useState(null);
	const [failedAttempts, setFailedAttempts] = useState(0);
	const [shownSteps, setShownSteps] = useState(new Set());
	const pageContextRef = useRef({});
	const setPageContext = useCallback((ctx) => {
		pageContextRef.current = ctx;
	}, []);

	useEffect(() => {
		const storageKey = getStorageKey(userId);
		if (!storageKey) {
			setSessionId(generateSessionId());
			return;
		}

		const savedSession = localStorage.getItem(storageKey);
		if (savedSession) {
			try {
				const {
					messages: savedMessages,
					sessionId: savedId,
					failedAttempts: savedFailed,
					shownSteps: savedShownSteps
				} = JSON.parse(savedSession);
				setMessages(savedMessages || []);
				setSessionId(savedId);
				setFailedAttempts(savedFailed || 0);
				if (Array.isArray(savedShownSteps)) {
					setShownSteps(new Set(savedShownSteps));
				}
			} catch (err) {
				console.error('Failed to load assistant history:', err);
				setSessionId(generateSessionId());
			}
		} else {
			setSessionId(generateSessionId());
		}
	}, [userId]);

	useEffect(() => {
		const storageKey = getStorageKey(userId);
		if (storageKey && sessionId && messages.length > 0) {
			localStorage.setItem(
				storageKey,
				JSON.stringify({
					messages,
					sessionId,
					failedAttempts,
					shownSteps: Array.from(shownSteps),
					timestamp: Date.now()
				})
			);
		}
	}, [messages, sessionId, failedAttempts, shownSteps, userId]);

	const clearHistory = useCallback(() => {
		setMessages([]);
		setFailedAttempts(0);
		setShownSteps(new Set());
		const storageKey = getStorageKey(userId);
		if (storageKey) {
			localStorage.removeItem(storageKey);
		}
		setSessionId(generateSessionId());
	}, [userId]);

	const triggerBotMessages = useCallback(
		(texts) => {
			setIsLoading(true);
			texts.forEach((text, index) => {
				setTimeout(
					() => {
						setMessages((prev) => [
							...prev,
							{
								id: Date.now() + index,
								role: 'assistant',
								content: text,
								timestamp: new Date().toISOString(),
								isWelcome: true
							}
						]);
						if (index === texts.length - 1) {
							setIsLoading(false);
						}
					},
					(index + 1) * 800
				);
			});
		},
		[setMessages, setIsLoading]
	);

	const sendMessage = async (content) => {
		const userMessage = {
			id: Date.now(),
			role: 'user',
			content,
			timestamp: new Date().toISOString()
		};
		setMessages((prev) => [...prev, userMessage]);
		setIsLoading(true);

		try {
			const context = {
				page: mode,
				shopData: currentShop
					? {
							id: currentShop.id,
							name: currentShop.name,
							plan: currentShop.plan,
							organisationId: currentShop.organisationId,
							enabled: currentShop.enabled,
							address: currentShop.address,
							contactEmail: currentShop.contactEmail,
							gmbConnection: currentShop.gmbConnection?.status || null,
							inventorySources: currentShop.inventorySources?.map((s) => ({
								kind: s.kind,
								connector: s.connector || s.pointOfSale,
								providerLabel: s.providerLabel,
								enabled: s.enabled
							})),
							stockInventorySource: currentShop.stockInventorySource
								? {
										connector:
											currentShop.stockInventorySource.connector || currentShop.stockInventorySource.pointOfSale,
										providerLabel: currentShop.stockInventorySource.providerLabel,
										enabled: currentShop.stockInventorySource.enabled
									}
								: null,
							productInventorySource: currentShop.productInventorySource
								? {
										connector:
											currentShop.productInventorySource.connector || currentShop.productInventorySource.pointOfSale,
										providerLabel: currentShop.productInventorySource.providerLabel,
										enabled: currentShop.productInventorySource.enabled
									}
								: null
						}
					: null,
				pageContext: pageContextRef.current
			};

			const response = await assistantApi.sendMessage({
				sessionId,
				message: content,
				context,
				conversationHistory: messages.slice(-10)
			});

			const assistantMessage = {
				id: Date.now() + 1,
				role: 'assistant',
				content: response.message,
				sources: response.sources || [],
				timestamp: new Date().toISOString()
			};
			setMessages((prev) => [...prev, assistantMessage]);

			setFailedAttempts(0);
		} catch (error) {
			console.error('Assistant error:', error);

			const newFailedAttempts = failedAttempts + 1;
			setFailedAttempts(newFailedAttempts);

			let errorContent = 'Sorry, I encountered an error. Please try again.';

			if (newFailedAttempts >= 3) {
				errorContent =
					"I'm having trouble responding right now. If you need immediate help, please contact our support team at support@near.st. They typically respond within 1 business day.";
			}

			const errorMessage = {
				id: Date.now() + 1,
				role: 'assistant',
				content: errorContent,
				timestamp: new Date().toISOString(),
				isError: true
			};
			setMessages((prev) => [...prev, errorMessage]);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<AssistantContext.Provider
			value={{
				messages,
				isOpen,
				setIsOpen,
				isMobileOpen,
				setIsMobileOpen,
				isLoading,
				sendMessage,
				clearHistory,
				sessionId,
				failedAttempts,
				currentShop,
				mode,
				setPageContext,
				triggerBotMessages,
				shownSteps,
				setShownSteps
			}}
		>
			{children}
		</AssistantContext.Provider>
	);
};

export const useAssistant = () => {
	const context = useContext(AssistantContext);
	if (!context) {
		throw new Error('useAssistant must be used within an AssistantProvider');
	}
	return context;
};
